import { Description as FileText, AddCircle as PlusCircle } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { PageHeader } from '@/components/page-header';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { getTextsWithScenes } from '@/lib/text-service';

export const Route = createFileRoute('/texts')({
  component: ChaptersPage,
});

function ChaptersContent() {
  const { novel } = useNovelRepository();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();

  const texts = useMemo(() => {
    return getTextsWithScenes(novel);
  }, [novel]);

  const isLoading = !novel;

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      <PageHeader
        title={t('texts.title')}
        description={t('texts.description')}
        actions={
          <Button
            component={Link}
            to="/editor"
            variant="contained"
            startIcon={<PlusCircle sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 2 }}
          >
            {t('texts.new_text')}
          </Button>
        }
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : texts.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
            textAlign: 'center',
            p: 4,
          }}
        >
          <FileText sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
          <Typography variant="h5" gutterBottom color="text.secondary">
            {t('texts.no_chapters')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('texts.create_first')}
          </Typography>
          <Button
            component={Link}
            to="/editor"
            variant="contained"
            startIcon={<PlusCircle sx={{ fontSize: 18 }} />}
          >
            {t('texts.start_writing')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {texts.map((text) => (
            <Grid key={text.id} size={resolveResponsive({ xs: 12, md: 6, lg: 4 })}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: 3,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px -10px rgba(0,0,0,0.15)',
                    borderColor: 'primary.light',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: '1.5rem',
                      mb: 1.5,
                      color: 'primary.main',
                    }}
                  >
                    {text.name || t('texts.untitled')}
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    <Chip
                      label={t('texts.scenes_count', { count: text.sceneIds.length })}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.6,
                    }}
                  >
                    {text.scratchpad || t('texts.no_content_yet')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    component={Link}
                    to={`/editor?textId=${text.id}`}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ borderRadius: 1.5 }}
                  >
                    {t('texts.edit_chapter')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

function ChaptersPage() {
  return (
    <AuthGuard>
      <ChaptersContent />
    </AuthGuard>
  );
}
